// src/components/Game.jsx
import { useQuery } from "@tanstack/react-query";
import { treadmill } from "ldrs";
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { getGameByCode } from "../api/game";
import { checkAsDiscount } from "../utils/resolveCode";
import { normalizeCode, pricingPath, savePendingDiscount } from "../utils/pendingDiscount";
import SJHeader from "../components/header/SJHeader";
import MKH from "../components/themes/MKH";
import SJ from "../components/themes/SJ";
import Tarbaa from "../components/themes/Tarbaa";
import MKHHeader from "../components/header/MKHHeader";
import TarbaaHeader from "../components/header/TarbaaHeader";

// Import encryption utilities
import { encryptData, decryptData } from "../funcs/encryption";
import * as webGmBridge from "../live/webGmBridge";
import { useGmLive } from "../live/useGmLive";
import GmLiveModal from "../components/live/GmLiveModal";

const Game = () => {
  const { code } = useParams();
  const encryptedLocalGame = localStorage.getItem("game");
  const [game, setGame] = useState(
    encryptedLocalGame ? decryptData(encryptedLocalGame) : null
  );
  const [theme, setTheme] = useState(null);
  // eslint-disable-next-line
  const [mediaCacheStatus, setMediaCacheStatus] = useState({
    total: 0,
    loaded: 0,
  });
  const cachedMediaUrls = useRef(new Set());
  const navigate = useNavigate();

  // No game behind this code. People also paste website discount codes here
  // (or open tarbaa.com/<code>): if it is one, send them to pricing with it
  // applied instead of "wrong code". Only a confirmed discount code counts.
  const leaveInvalidCode = async (noSuchGame) => {
    if (noSuchGame) {
      const asDiscount = await checkAsDiscount(normalizeCode(code));
      if (asDiscount.kind === "discount" && asDiscount.verified) {
        savePendingDiscount(asDiscount.code);
        toast.success("هذا كود خصم وليس كود لعبة، طبّقناه لك على الباقات");
        navigate(pricingPath(asDiscount.code), { replace: true });
        return;
      }
    }
    toast.error("الكود غير صحيح او تم استعماله من قبل");
    navigate("/");
  };

  // ── Live broadcast (web game master) ──
  const [liveModalOpen, setLiveModalOpen] = useState(false);
  const gmLive = useGmLive();

  // After a full page refresh, silently re-claim a stored live session for
  // this game and push a fresh board snapshot.
  useEffect(() => {
    if (game?.teams?.length) {
      webGmBridge.resumeFromStorage(
        game?.codeInfo?.code || game?._id,
        webGmBridge.buildBoardState(game)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?._id]);

  // Mirror the board to spectators on every change (scores, answered tiles,
  // turn). Debounced + no-op inside the bridge when not broadcasting.
  useEffect(() => {
    if (!webGmBridge.isActive() || !game?.teams?.length) return;
    webGmBridge.emitState(webGmBridge.buildBoardState(game));
  }, [game]);

  treadmill.register();

  const { data, isError, isLoading, error } = useQuery({
    queryKey: ["game", code],
    queryFn: () => getGameByCode(code),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Function to extract all media URLs from the game object
  const extractMediaUrls = (gameData) => {
    if (!gameData?.modes) return [];

    const urls = [];

    gameData.modes.forEach((mode) => {
      if (!mode.questions) return;

      mode.questions.forEach((questionObj) => {
        const question = questionObj.question || questionObj.customQuestion;
        if (!question) return;

        // Question media
        if (question.questionImage) urls.push(question.questionImage);
        if (question.questionVideo) urls.push(question.questionVideo);
        if (question.questionAudio) urls.push(question.questionAudio);

        // Answer media
        if (question.answerImage) urls.push(question.answerImage);
        if (question.answerVideo) urls.push(question.answerVideo);
        if (question.answerAudio) urls.push(question.answerAudio);
      });
    });

    return urls.filter((url) => url && url.length > 10);
  };

  // Function to preload images
  const preloadImage = (url) => {
    return new Promise((resolve, reject) => {
      if (cachedMediaUrls.current.has(url)) {
        resolve(url);
        return;
      }

      const img = new Image();
      img.src = url;
      img.onload = () => {
        cachedMediaUrls.current.add(url);
        setMediaCacheStatus((prev) => ({
          ...prev,
          loaded: prev.loaded + 1,
        }));
        resolve(url);
      };
      img.onerror = () => {
        // Still mark as loaded to avoid hanging
        setMediaCacheStatus((prev) => ({
          ...prev,
          loaded: prev.loaded + 1,
        }));
        reject(new Error(`Failed to load image: ${url}`));
      };
    });
  };

  // Function to preload audio/video
  const preloadMedia = (url) => {
    return new Promise((resolve, reject) => {
      if (cachedMediaUrls.current.has(url)) {
        resolve(url);
        return;
      }

      // For video/audio URLs, we'll just make a HEAD request
      fetch(url, { method: "HEAD" })
        .then(() => {
          cachedMediaUrls.current.add(url);
          setMediaCacheStatus((prev) => ({
            ...prev,
            loaded: prev.loaded + 1,
          }));
          resolve(url);
        })
        .catch((error) => {
          // Still mark as loaded to avoid hanging
          setMediaCacheStatus((prev) => ({
            ...prev,
            loaded: prev.loaded + 1,
          }));
          reject(error);
        });
    });
  };

  // Check if media for this game has already been cached
  const isMediaCached = (gameData) => {
    if (!gameData?.codeInfo?.code) return false;
    return (
      localStorage.getItem(`media-cached-${gameData.codeInfo.code}`) === "true"
    );
  };

  // Mark media as cached for this game
  const markMediaAsCached = (gameData) => {
    if (!gameData?.codeInfo?.code) return;
    localStorage.setItem(`media-cached-${gameData.codeInfo.code}`, "true");
  };

  // Preload all media assets
  const preloadAllMedia = async (gameData) => {
    // Skip if media for this game has already been cached
    if (isMediaCached(gameData)) {
      console.log("Media already cached for this game");
      return;
    }

    const mediaUrls = extractMediaUrls(gameData);

    if (mediaUrls.length === 0) {
      markMediaAsCached(gameData);
      return;
    }

    setMediaCacheStatus({
      total: mediaUrls.length,
      loaded: 0,
    });

    const preloadPromises = mediaUrls.map((url) => {
      if (!url || url.length < 10) return Promise.resolve();

      if (url.match(/\.(jpeg|jpg|gif|png)$/i)) {
        return preloadImage(url);
      } else {
        return preloadMedia(url);
      }
    });

    try {
      await Promise.allSettled(preloadPromises);
      console.log("All media preloaded successfully");
      markMediaAsCached(gameData);
    } catch (error) {
      console.error("Error preloading media:", error);
    }
  };

  useEffect(() => {
    const encryptedGame = localStorage.getItem("game");
    setGame(encryptedGame ? decryptData(encryptedGame) : null);

    if (
      code === "old" &&
      encryptedLocalGame !== null &&
      encryptedLocalGame !== undefined
    ) {
      // If we're loading an old game, preload its media if not already cached
      if (encryptedGame) {
        const gameData = decryptData(encryptedGame);
        preloadAllMedia(gameData);
      }
      return;
    } else {
      if (!isLoading) {
        if (data) {
          if (game && game?.codeInfo?.code === code) {
            // Game already loaded, preload media if not already cached
            preloadAllMedia(game);
            return;
          }
          // Clone the data to avoid mutating original
          const clonedData = { ...data };

          // Set currentTurn to true for a random team
          const randomIndex = Math.floor(
            Math.random() * clonedData.teams.length
          );
          clonedData.teams = clonedData.teams.map((team, index) => ({
            ...team,
            currentTurn: index === randomIndex,
          }));

          // Encrypt and save to local storage
          const encryptedData = encryptData(clonedData);
          localStorage.setItem("game", encryptedData);

          // Remove anything in the localstorage that starts with faded or shuffled
          Object.keys(localStorage).forEach((key) => {
            if (key.startsWith("faded") || key.startsWith("shuffled")) {
              localStorage.removeItem(key);
            }
          });

          // Start preloading media
          preloadAllMedia(clonedData);

          // Optionally update state without reloading
          setGame(clonedData);
          window.location.reload();
        } else {
          const invalidCode =
            error?.response?.data?.message === "invalid code";
          if (invalidCode) {
            localStorage.removeItem("game");
          }

          if (!game || game === null) {
            localStorage.removeItem("game");
            // Remove anything in the localstorage that starts with faded or shuffled
            Object.keys(localStorage).forEach((key) => {
              if (key.startsWith("faded") || key.startsWith("shuffled")) {
                localStorage.removeItem(key);
              }
            });
          }

          if (invalidCode || !game) {
            leaveInvalidCode(invalidCode);
          }
        }
      }
    }
    // eslint-disable-next-line
  }, [data, isError]);

  // If a game is loaded after the initial render, preload its media if not cached
  useEffect(() => {
    if (game && !isLoading) {
      preloadAllMedia(game);
    }
    // eslint-disable-next-line
  }, [game]);

  useEffect(() => {
    const theme = localStorage.getItem("theme");
    if (theme) {
      setTheme(theme);
    }
  }, []);

  useEffect(() => {
    // Prevent right-click context menu
    const handleContextMenu = (e) => {
      e.preventDefault();
    };

    document.addEventListener("contextmenu", handleContextMenu);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, []);

  if (isLoading && !game) {
    return (
      <div className="h-[100vh] w-full flex justify-center items-center flex-col gap-5">
        <h1 className="text-3xl text-primaryButton">...جاري التحميل</h1>
        <l-treadmill size="120" speed="1.1" color="#516797" />
      </div>
    );
  }

  // Show loading status if media is being cached
  // const showMediaCacheStatus =
  //   mediaCacheStatus.total > 0 &&
  //   mediaCacheStatus.loaded < mediaCacheStatus.total;

  return (
    <div className="h-full w-full flex flex-col font-sans select-none">
      {/* {showMediaCacheStatus && (
        <div className="fixed top-0 right-0 bg-gray-800 bg-opacity-80 text-white p-2 rounded-bl-lg z-50">
          جاري تحميل الوسائط: {mediaCacheStatus.loaded}/{mediaCacheStatus.total}
        </div>
      )} */}

      {theme === "MKH" ? (
        <>
          <MKHHeader theme={theme} game={game} setGame={setGame} />
          <MKH theme={theme} game={game} setGame={setGame} />
        </>
      ) : theme === "SJ" ? (
        <>
          <SJHeader theme={theme} game={game} setGame={setGame} />
          <SJ theme={theme} game={game} setGame={setGame} />
        </>
      ) : (
        <>
          <TarbaaHeader
            theme={theme}
            game={game}
            setGame={setGame}
            live={{
              active: gmLive.active,
              viewers: gmLive.presence.total,
              onOpen: () => setLiveModalOpen(true),
            }}
          />
          <Tarbaa theme={theme} game={game} setGame={setGame} />
        </>
      )}
      <GmLiveModal
        open={liveModalOpen}
        onClose={() => setLiveModalOpen(false)}
        game={game}
      />
    </div>
  );
};

export default Game;
